import Swal from 'sweetalert2';
import type { Notification } from '@models/community/notification.model';
import { resolveCommunityEventNotificationChrome } from '@notifications/functions/community-event-notification.functions';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildToastHtml(notification: Notification): string {
  const chrome = resolveCommunityEventNotificationChrome(notification);
  const title = escapeHtml(notification.content?.title ?? '');
  const body = escapeHtml(notification.content?.body ?? '');
  const eventName = chrome.eventName ? escapeHtml(chrome.eventName) : null;

  const badges: string[] = [];
  if (!notification.readInd) {
    badges.push('<span class="badge badge-soft-warning">Unread</span>');
  }
  if (chrome.applyR7) {
    for (const badge of chrome.badges) {
      badges.push(`<span class="${escapeHtml(badge.cssClass)}">${escapeHtml(badge.label)}</span>`);
    }
    if (chrome.sourceLabel) {
      badges.push(`<span class="text-muted font-size-14">${escapeHtml(chrome.sourceLabel)}</span>`);
    }
  }

  const eventNameHtml = eventName
    ? `<h6 class="text-primary mb-1 rwe-notification-toast__event">${eventName}</h6>`
    : '';

  const seeDetailsHtml =
    notification.type === 'CommunityEvent' && chrome.canSeeDetails
      ? `<button type="button" class="btn btn-link text-primary p-0 rwe-notification-toast__details" data-see-details>
           See details <i class="bx bx-chevron-right"></i>
         </button>`
      : '';

  return `
    <div class="rwe-notification-toast__card text-start">
      <div class="d-flex gap-2 align-items-center flex-wrap mb-2">
        ${badges.join('')}
      </div>
      <h5 class="fw-bolder mb-1 rwe-notification-toast__title">${title}</h5>
      ${eventNameHtml}
      <p class="mb-2 rwe-notification-toast__text">${body}</p>
      ${seeDetailsHtml}
    </div>
  `;
}

export function showIncomingNotificationToast(
  notification: Notification,
  options?: { onSeeDetails?: () => void },
): void {
  const chrome = resolveCommunityEventNotificationChrome(notification);
  const showDetails =
    !!options?.onSeeDetails && notification.type === 'CommunityEvent' && chrome.canSeeDetails;

  void Swal.fire({
    toast: true,
    position: 'bottom-end',
    showConfirmButton: false,
    showCloseButton: true,
    timer: 7000,
    timerProgressBar: true,
    backdrop: false,
    width: '26rem',
    customClass: {
      popup: 'rwe-notification-toast',
      htmlContainer: 'rwe-notification-toast__html',
      closeButton: 'rwe-notification-toast__close',
      timerProgressBar: 'rwe-notification-toast__timer',
    },
    html: buildToastHtml(notification),
    didOpen: (popup) => {
      if (!showDetails || !options?.onSeeDetails) {
        return;
      }
      const button = popup.querySelector('[data-see-details]');
      button?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        options.onSeeDetails!();
        Swal.close();
      });
    },
  });
}
